import React, { useState, useEffect } from 'react';
import { apiFetch } from '../../../utils/apiConfig';
import { getAuthHeaders } from '../../../utils/authUtils';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from '../../CentralSnackbar';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  IconButton,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, Refresh as RefreshIcon, Computer as WorkstationsIcon } from '@mui/icons-material';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:25000';

const Workstations = () => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useSnackbar();
  const [workstations, setWorkstations] = useState([]);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [modalityFilter, setModalityFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [deletingLoading, setDeletingLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogEditWorkstation, setDialogEditWorkstation] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Formular-States für Add/Edit Dialog
  const [formData, setFormData] = useState({
    short_name: '',
    name: '',
    modality: '',
    is_active: true
  });

  // Reset form data when dialog opens/closes
  const resetFormData = () => {
    setFormData({
      short_name: '',
      name: '',
      modality: '',
      is_active: true
    });
  };

  useEffect(() => {
    fetchWorkstations();
    fetchUsers();
    // eslint-disable-next-line
  }, [search, modalityFilter]);

  // Lade Arbeitsplätze vom Backend
  const fetchWorkstations = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (modalityFilter) params.append('modality', modalityFilter);
      const res = await apiFetch(`/admin/workstations?${params.toString()}`);
      if (res.ok) {
        let data = await res.json();
        // Sortiere nach short_name
        data = data.sort((a, b) => {
          if ((a.short_name || '').toLowerCase() < (b.short_name || '').toLowerCase()) return -1;
          if ((a.short_name || '').toLowerCase() > (b.short_name || '').toLowerCase()) return 1;
          return 0;
        });
        setWorkstations(data);
      }
    } catch (e) {
      showError(t('admin.reportingManagement.workstations.loadError'), 'ERR-WS-001');
    } finally {
      setLoading(false);
    }
  };

  // Lade Benutzer vom Backend für die Anzeige
  const fetchUsers = async () => {
    try {
      const res = await apiFetch('/admin/users');
      if (res.ok) {
        const data = await res.json();
        setUsers(data);
      }
    } catch (e) {
      console.error('Error loading users:', e);
    }
  };

  // Helper: Get user name by ID
  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    if (!user) return `User ${userId}`;
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    return user.username;
  };

  // --- UI-RENDER ---
  const hasSelected = selected.length > 0;
  const exactlyOneSelected = selected.length === 1;

  // Verfügbare Modalitäten aus den Daten extrahieren
  const availableModalities = [...new Set(workstations.map(w => w.modality).filter(Boolean))].sort();

  // Checkbox-Handler
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelected(workstations.map(ws => ws.id));
    } else {
      setSelected([]);
    }
  };

  const handleSelect = (wsId) => {
    setSelected(prev => 
      prev.includes(wsId) 
        ? prev.filter(id => id !== wsId)
        : [...prev, wsId]
    );
  };

  // Button-Handler
  const handleOpenAdd = () => {
    resetFormData();
    setDialogEditWorkstation(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = () => {
    const selectedWs = workstations.find(ws => ws.id === selected[0]);
    setDialogEditWorkstation(selectedWs);
    setFormData({
      short_name: selectedWs.short_name || '',
      name: selectedWs.name || '',
      modality: selectedWs.modality || '',
      is_active: selectedWs.is_active !== false
    });
    setDialogOpen(true);
  };

  const handleDelete = () => {
    if (selected.length === 1) {
      const selectedWs = workstations.find(ws => ws.id === selected[0]);
      setDeleteTarget(selectedWs);
    } else {
      setDeleteTarget({ count: selected.length });
    }
    setDeleteDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      const url = dialogEditWorkstation 
        ? `${API_BASE}/admin/workstations/${dialogEditWorkstation.id}`
        : `${API_BASE}/admin/workstations`;
      
      const method = dialogEditWorkstation ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setDialogOpen(false);
        showSuccess(dialogEditWorkstation ? t('common.saveSuccess') : t('common.createSuccess'));
        fetchWorkstations();
        setSelected([]);
      } else {
        const errorData = await response.json();
        showError(errorData.detail || t('admin.reportingManagement.workstations.saveError'), 'ERR-WS-002');
      }
    } catch (error) {
      showError(t('admin.reportingManagement.workstations.saveError'), 'ERR-WS-003');
    }
  };

  const handleConfirmDelete = async () => {
    try {
      setDeletingLoading(true);
      
      let deletedCount = 0;
      let hasErrors = false;

      // Lösche alle ausgewählten Arbeitsplätze einzeln
      for (const wsId of selected) {
        try {
          const response = await fetch(`${API_BASE}/admin/workstations/${wsId}`, {
            method: 'DELETE',
            headers: {
              ...getAuthHeaders()
            }
          });

          if (response.ok) {
            deletedCount++;
          } else {
            hasErrors = true;
          }
        } catch {
          hasErrors = true;
        }
      }

      if (deletedCount > 0) {
        setDeleteDialogOpen(false);
        if (selected.length === 1) {
          showSuccess(t('common.deleteSuccess'));
        } else {
          showSuccess(t('admin.reportingManagement.workstations.deleteMultipleSuccess', { count: deletedCount }));
        }
        fetchWorkstations();
        setSelected([]);
      }

      if (hasErrors) {
        showError(t('admin.reportingManagement.workstations.deletePartialSuccess', { 
          success: deletedCount, 
          total: selected.length 
        }), 'ERR-WS-004');
      }
    } catch (error) {
      showError(t('admin.reportingManagement.workstations.deleteError'), 'ERR-WS-005');
    } finally {
      setDeletingLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WorkstationsIcon color="primary" />
          {t('admin.reportingManagement.workstations.title')}
        </Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAdd}
            className="button"
            disabled={loading || deletingLoading}
          >
            {t('common.add')}
          </Button>
          <Tooltip title={t('common.refresh')}>
            <IconButton onClick={fetchWorkstations} disabled={loading || deletingLoading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Aktionsleiste */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Typography variant="subtitle2" sx={{ mr: 2 }}>
              {hasSelected
                ? t('admin.reportingManagement.workstations.selected', { count: selected.length })
                : t('admin.reportingManagement.workstations.noSelected')
              }
            </Typography>
            <Divider orientation="vertical" flexItem />
            <Tooltip title={t('common.edit')}>
              <span>
                <IconButton
                  size="small"
                  onClick={handleOpenEdit}
                  disabled={!exactlyOneSelected || loading || deletingLoading}
                >
                  <EditIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t('common.delete')}>
              <span>
                <IconButton
                  size="small"
                  color="error"
                  onClick={handleDelete}
                  disabled={!hasSelected || loading || deletingLoading}
                >
                  <DeleteIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          
          {/* Filter-Bereich */}
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>{t('admin.reportingManagement.workstations.modality')}</InputLabel>
              <Select
                value={modalityFilter}
                label={t('admin.reportingManagement.workstations.modality')}
                onChange={(e) => setModalityFilter(e.target.value)}
              >
                <MenuItem value="">
                  <em>{t('common.all')}</em>
                </MenuItem>
                {availableModalities.map((modality) => (
                  <MenuItem key={modality} value={modality}>
                    {modality}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <TextField
              label={t('admin.reportingManagement.workstations.search')}
              value={search}
              onChange={e => setSearch(e.target.value)}
              size="small"
              sx={{ minWidth: 220 }}
            />
          </Box>
        </Box>
      </Paper>

      {/* Tabelle */}
      <Paper sx={{ width: '100%' }}>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 420px)' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ position: 'sticky', top: 0, backgroundColor: (theme) => theme.palette.background.paper, zIndex: 2 }}>
                  <Checkbox
                    indeterminate={selected.length > 0 && selected.length < workstations.length}
                    checked={workstations.length > 0 && selected.length === workstations.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell sx={{ position: 'sticky', top: 0, backgroundColor: (theme) => theme.palette.background.paper, zIndex: 2 }}>{t('admin.reportingManagement.workstations.short_name')}</TableCell>
                <TableCell sx={{ position: 'sticky', top: 0, backgroundColor: (theme) => theme.palette.background.paper, zIndex: 2 }}>{t('admin.reportingManagement.workstations.name')}</TableCell>
                <TableCell sx={{ position: 'sticky', top: 0, backgroundColor: (theme) => theme.palette.background.paper, zIndex: 2 }}>{t('admin.reportingManagement.workstations.modality')}</TableCell>
                <TableCell sx={{ position: 'sticky', top: 0, backgroundColor: (theme) => theme.palette.background.paper, zIndex: 2 }}>{t('admin.reportingManagement.workstations.users_logged_in')}</TableCell>
                <TableCell sx={{ position: 'sticky', top: 0, backgroundColor: (theme) => theme.palette.background.paper, zIndex: 2 }}>{t('admin.reportingManagement.workstations.is_active')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : workstations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    {t('admin.reportingManagement.workstations.noResults')}
                  </TableCell>
                </TableRow>
              ) : (
                workstations.map(ws => (
                  <TableRow key={ws.id} selected={selected.includes(ws.id)}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selected.includes(ws.id)}
                        onChange={() => handleSelect(ws.id)}
                      />
                    </TableCell>
                    <TableCell>{ws.short_name}</TableCell>
                    <TableCell>{ws.name}</TableCell>
                    <TableCell>{ws.modality || '-'}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {ws.users_logged_in && ws.users_logged_in.length > 0 ? (
                          ws.users_logged_in.map(userId => (
                            <Chip 
                              key={userId} 
                              label={getUserName(userId)} 
                              size="small" 
                              color="primary" 
                              variant="outlined"
                            />
                          ))
                        ) : (
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{ws.is_active ? t('common.active') : t('common.inactive')}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogEditWorkstation ? t('admin.reportingManagement.workstations.editTitle') : t('admin.reportingManagement.workstations.addTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('admin.reportingManagement.workstations.description')}
          </DialogContentText>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label={t('admin.reportingManagement.workstations.short_name')}
              value={formData.short_name}
              onChange={(e) => setFormData({ ...formData, short_name: e.target.value })}
              fullWidth
              required
              size="small"
              helperText={t('admin.reportingManagement.workstations.short_name_help')}
            />
            
            <TextField
              label={t('admin.reportingManagement.workstations.name')}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
              size="small"
              helperText={t('admin.reportingManagement.workstations.name_help')}
            />
            
            <TextField
              label={t('admin.reportingManagement.workstations.modality')}
              value={formData.modality}
              onChange={(e) => setFormData({ ...formData, modality: e.target.value })}
              fullWidth
              size="small"
              helperText={t('admin.reportingManagement.workstations.modality_help')}
            />
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Checkbox
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                size="small"
              />
              <Typography variant="body2">
                {t('admin.reportingManagement.workstations.is_active')}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} className="button-cancel">
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={handleSave} 
            className="button"
            disabled={!formData.short_name || !formData.name}
          >
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => !deletingLoading && setDeleteDialogOpen(false)}>
        <DialogTitle>
          {t('common.delete')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {selected.length === 1 
              ? (() => {
                  const ws = workstations.find(w => w.id === selected[0]);
                  return t('admin.reportingManagement.workstations.deleteConfirmSingle', { 
                    name: ws?.name || ws?.short_name || 'Arbeitsplatz'
                  });
                })()
              : t('admin.reportingManagement.workstations.deleteConfirmMultiple', { count: selected.length })
            }
          </DialogContentText>
          {deletingLoading && (
            <Box display="flex" alignItems="center" gap={2} mt={2}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                {t('common.deleting')}...
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialogOpen(false)} 
            className="button-cancel"
            disabled={deletingLoading}
          >
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            className="button-delete"
            disabled={deletingLoading}
          >
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Workstations;
